import { useState, type ChangeEvent } from "react";
import { toast } from "react-hot-toast";
import { User, KeyRound, Camera, Check, Key } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { UserProfile } from "../mockSettings";

interface ProfileSectionProps {
  profile: UserProfile;
  onChange: (patch: Partial<UserProfile>) => void;
}

export function ProfileSection({ profile, onChange }: ProfileSectionProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleAvatarPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange({ avatarUrl: URL.createObjectURL(file) });
  };

  const handleSaveProfile = () => {
    toast.success("Profile saved");
  };

  const handleChangePassword = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Fill in all password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    toast.success("Password updated");
  };

  return (
    <div className="space-y-5">
      {/* Profile Details Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <User className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Personal Account Profile
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Manage your identity</span>
        </div>

        <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-4">
          <div className="flex items-center gap-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs">
            {profile.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt="Your avatar"
                className="h-12 w-12 rounded-xl object-cover shadow-xs border border-slate-200/80 dark:border-slate-700"
              />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-bold text-primary shadow-xs">
                {profile.avatarInitials}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <Label htmlFor="avatar-upload" className="cursor-pointer">
                <span className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 text-xs font-semibold text-slate-700 dark:text-slate-200 shadow-xs transition-colors hover:bg-slate-100 dark:hover:bg-slate-700">
                  <Camera className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
                  Change photo
                </span>
              </Label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleAvatarPick}
              />
              <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500 font-medium">JPG or PNG, square works best.</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="profile-name" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Full name</Label>
              <Input
                id="profile-name"
                value={profile.fullName}
                onChange={(e) => onChange({ fullName: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="profile-email" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Email address</Label>
              <Input
                id="profile-email"
                type="email"
                value={profile.email}
                onChange={(e) => onChange({ email: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <Button className="h-9 px-5 text-sm font-semibold rounded-xl shadow-xs gap-1.5" onClick={handleSaveProfile}>
              <Check className="h-4 w-4" />
              Save changes
            </Button>
          </div>
        </div>
      </Card>

      {/* Password Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <KeyRound className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Security &amp; Password
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Keep account secure</span>
        </div>

        <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
          <div>
            <Label htmlFor="current-password" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Current password</Label>
            <Input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="new-password" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-[10px] font-semibold text-slate-600 dark:text-slate-400">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <Button variant="outline" className="h-9 px-5 text-sm font-semibold rounded-xl shadow-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 gap-1.5" onClick={handleChangePassword}>
              <Key className="h-3.5 w-3.5" />
              Update password
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
